import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiBaseUrl } from '../../../core/config/api';
import type { StatutPlanningRowBase } from '../../../config';
import { statuts } from '../../../config';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import { getPlanningServiceKeyFromBuildingType } from '../utils/buildingServiceMapping';

export const DEFAULT_PLANNING_NOM = 'PERSONNELS';

export const PLANNING_GROUPE_KEYS = ['A', 'B', 'C', 'D'] as const;
export type PlanningGroupeKey = (typeof PLANNING_GROUPE_KEYS)[number];

export interface PlanningPersonnelRow {
  id: number;
  nom: string;
  prenom: string;
  grade: string;
  grade_code?: string;
  grade_ordre?: number | null;
  planning_statut_nom?: string | null;
  batiment_id?: number | null;
  planning_weeks_json?: unknown;
  /** Groupe opérationnel A–D (`player_staff.groupe`) */
  groupe?: string | null;
}

/** Normalise la valeur API (A, GR-B, etc.) pour les filtres. */
export function normalizePlanningGroupeValue(raw: string | null | undefined): PlanningGroupeKey | null {
  if (raw == null || String(raw).trim() === '') return null;
  const u = String(raw).trim().toUpperCase();
  if (u === 'A' || u === 'B' || u === 'C' || u === 'D') return u;
  const m = u.match(/^(?:GR[-\s]?|GROUPE[-\s]?)?([ABCD])(?:\b|$)/);
  if (m) return m[1].toUpperCase() as PlanningGroupeKey;
  return null;
}

interface OperationalPlanningModalProps {
  isOpen: boolean;
  onClose: () => void;
  buildingId?: number | null;
  buildingName?: string | null;
  /** Type de bâtiment (carte / fiche) pour filtrer les statuts planning (Pompiers, SAMU, FDO). */
  buildingType?: string | null;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addLocalDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return startOfLocalDay(x);
}

function startOfWeekMonday(d: Date): Date {
  const x = startOfLocalDay(d);
  const dow = x.getDay();
  const daysSinceMonday = (dow + 6) % 7;
  return addLocalDays(x, -daysSinceMonday);
}

function toLocalDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseLocalDateInput(iso: string): Date {
  const [y, m, d] = iso.split('-').map((x) => parseInt(x, 10));
  return startOfLocalDay(new Date(y, m - 1, d));
}

function formatWeekBannerFr(weekStart: Date): string {
  const weekEnd = addLocalDays(weekStart, 6);
  const optsLong: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };
  const a = weekStart.toLocaleDateString('fr-FR', optsLong);
  const b = weekEnd.toLocaleDateString('fr-FR', optsLong);
  return `Semaine du ${a.charAt(0).toUpperCase() + a.slice(1)} au ${b.charAt(0).toUpperCase() + b.slice(1)}`;
}

function formatDayHeaderFr(d: Date): { weekday: string; dayMonth: string } {
  return {
    weekday: d.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', ''),
    dayMonth: d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }).replace('.', ''),
  };
}

function sortPlanningPersonnel(rows: PlanningPersonnelRow[]): PlanningPersonnelRow[] {
  return [...rows].sort((x, y) => {
    const ox = x.grade_ordre != null && !Number.isNaN(Number(x.grade_ordre)) ? Number(x.grade_ordre) : 999_999;
    const oy = y.grade_ordre != null && !Number.isNaN(Number(y.grade_ordre)) ? Number(y.grade_ordre) : 999_999;
    if (ox !== oy) return ox - oy;
    const g = (x.grade || '').localeCompare(y.grade || '', 'fr', { sensitivity: 'base' });
    if (g !== 0) return g;
    const nx = `${x.nom} ${x.prenom}`.trim();
    const ny = `${y.nom} ${y.prenom}`.trim();
    return nx.localeCompare(ny, 'fr', { sensitivity: 'base' });
  });
}

function sortPlanningLegendRows(rows: StatutPlanningRowBase[]): StatutPlanningRowBase[] {
  return [...rows].sort((a, b) => {
    const pa = a.prio != null ? a.prio : 9999;
    const pb = b.prio != null ? b.prio : 9999;
    if (pa !== pb) return pa - pb;
    return a.nom.localeCompare(b.nom, 'fr');
  });
}

function resolvePlanningColors(
  planningNom: string | null | undefined,
  serviceLegend: readonly StatutPlanningRowBase[]
): {
  backgroundColor: string;
  textColor: string;
} {
  const u = (planningNom || DEFAULT_PLANNING_NOM).trim().toUpperCase();
  const inSvc = serviceLegend.find((p) => p.nom.toUpperCase() === u);
  if (inSvc) return inSvc.couleurs;
  const s = statuts.statuts_planning.find((p) => p.nom.toUpperCase() === u);
  if (s) return s.couleurs;
  return { backgroundColor: '#f97316', textColor: '#fff' };
}

/** Code court pour l’impression (affiche `code` sinon l’`id` config, comme la légende à l’écran). */
function planningCodeLabel(
  planningNom: string | null | undefined,
  serviceLegend: readonly StatutPlanningRowBase[]
): string {
  const u = (planningNom || DEFAULT_PLANNING_NOM).trim().toUpperCase();
  const inSvc = serviceLegend.find((p) => p.nom.toUpperCase() === u);
  if (inSvc) {
    const c = inSvc.code?.trim();
    return c || String(inSvc.id);
  }
  const s = statuts.statuts_planning.find((p) => p.nom.toUpperCase() === u);
  if (s) {
    const c = s.code?.trim();
    return c || String(s.id);
  }
  const raw = (planningNom || DEFAULT_PLANNING_NOM).trim();
  return raw.length > 14 ? `${raw.slice(0, 12)}…` : raw;
}

const printColorAdjustStyle: React.CSSProperties = {
  WebkitPrintColorAdjust: 'exact',
  printColorAdjust: 'exact',
};

/** Contour intérieur double (clair + foncé) pour repérer le jour J sur n’importe quelle couleur de statut. */
const todayColumnCellBoxShadow = 'inset 0 0 0 2px #fffbeb, inset 0 0 0 6px #b45309';

/** Légende globale (tous services) : une entrée par libellé `nom`. */
function dedupePlanningLegendAllServices(): StatutPlanningRowBase[] {
  const byNom = new Map<string, StatutPlanningRowBase>();
  for (const s of statuts.statuts_planning) {
    const k = s.nom.trim().toUpperCase();
    if (!byNom.has(k)) {
      const { service: _omit, ...rest } = s;
      byNom.set(k, rest);
    }
  }
  return sortPlanningLegendRows(Array.from(byNom.values()));
}

function parsePlanningWeeksJson(raw: unknown): Record<string, string[]> {
  if (raw == null) return {};
  let o: unknown = raw;
  if (typeof raw === 'string') {
    try {
      o = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (typeof o !== 'object' || o === null || Array.isArray(o)) return {};
  const out: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(o as Record<string, unknown>)) {
    if (Array.isArray(v) && v.length === 7) {
      out[k] = v.map((x) => {
        const s = String(x ?? DEFAULT_PLANNING_NOM).trim();
        return s || DEFAULT_PLANNING_NOM;
      });
    }
  }
  return out;
}

/**
 * Libellé planning pour une date locale : jour dans la semaine du lundi,
 * avec repli sur la semaine précédente puis `planning_statut_nom` (comme la grille planning).
 */
export function getPlanningLabelForLocalDate(
  row: { planning_weeks_json?: unknown; planning_statut_nom?: string | null },
  d: Date = new Date()
): string {
  const weeks = parsePlanningWeeksJson(row.planning_weeks_json);
  const monday = startOfWeekMonday(d);
  const weekKey = toLocalDateInputValue(monday);
  const prevWeekKey = toLocalDateInputValue(addLocalDays(monday, -7));
  const idx = (startOfLocalDay(d).getDay() + 6) % 7;

  const pick = (arr: string[] | undefined): string | null => {
    if (!arr || arr.length !== 7) return null;
    const s = String(arr[idx] ?? '').trim();
    return s || null;
  };

  const fromWeek = pick(weeks[weekKey]) ?? pick(weeks[prevWeekKey]);
  if (fromWeek) return fromWeek;

  const fb = (row.planning_statut_nom && String(row.planning_statut_nom).trim()) || DEFAULT_PLANNING_NOM;
  return fb || DEFAULT_PLANNING_NOM;
}

function fill7FromNom(nom: string | null | undefined): string[] {
  const n = (nom && String(nom).trim()) || DEFAULT_PLANNING_NOM;
  return Array.from({ length: 7 }, () => n);
}

/** Jours effectifs : semaine enregistrée → sinon copie semaine précédente → sinon `planning_statut_nom` sur 7 jours. */
export function effectiveWeekDays(
  row: PlanningPersonnelRow,
  weekKey: string,
  prevWeekKey: string
): string[] {
  const weeks = parsePlanningWeeksJson(row.planning_weeks_json);
  if (weeks[weekKey]?.length === 7) return weeks[weekKey];
  if (weeks[prevWeekKey]?.length === 7) return [...weeks[prevWeekKey]];
  return fill7FromNom(row.planning_statut_nom);
}

const WEEK_DAY_COUNT = 7;

const OperationalPlanningModal: React.FC<OperationalPlanningModalProps> = ({
  isOpen,
  onClose,
  buildingId = null,
  buildingName = null,
  buildingType = null,
}) => {
  const { t } = usePageTranslation('game', 'Map');
  const [day, setDay] = useState(() => startOfLocalDay(new Date()));
  const [rows, setRows] = useState<PlanningPersonnelRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatut, setSelectedStatut] = useState(DEFAULT_PLANNING_NOM);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [persistError, setPersistError] = useState<string | null>(null);

  const weekStart = useMemo(() => startOfWeekMonday(day), [day]);
  const weekKey = useMemo(() => toLocalDateInputValue(weekStart), [weekStart]);
  const prevWeekKey = useMemo(() => toLocalDateInputValue(addLocalDays(weekStart, -7)), [weekStart]);

  const dayColumns = useMemo(
    () => Array.from({ length: WEEK_DAY_COUNT }, (_, i) => addLocalDays(weekStart, i)),
    [weekStart]
  );

  const todayStart = startOfLocalDay(new Date()).getTime();

  const filteredRows = useMemo(() => {
    if (buildingId == null) return rows;
    const bid = Number(buildingId);
    return rows.filter((r) => r.batiment_id != null && Number(r.batiment_id) === bid);
  }, [rows, buildingId]);

  const sorted = useMemo(() => sortPlanningPersonnel(filteredRows), [filteredRows]);

  const [filterGrade, setFilterGrade] = useState('');
  const [filterGroupe, setFilterGroupe] = useState<'' | PlanningGroupeKey>('');

  useEffect(() => {
    if (!isOpen) return;
    setFilterGrade('');
    setFilterGroupe('');
  }, [isOpen]);

  const gradeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of sorted) {
      const g = (r.grade || '').trim();
      if (g) set.add(g);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [sorted]);

  useEffect(() => {
    if (filterGrade !== '' && !gradeOptions.includes(filterGrade)) {
      setFilterGrade('');
    }
  }, [filterGrade, gradeOptions]);

  const displayRows = useMemo(() => {
    return sorted.filter((r) => {
      if (filterGrade !== '' && (r.grade || '').trim() !== filterGrade) return false;
      if (filterGroupe !== '') {
        const g = normalizePlanningGroupeValue(r.groupe);
        if (g !== filterGroupe) return false;
      }
      return true;
    });
  }, [sorted, filterGrade, filterGroupe]);

  const filterSummaryPrint = useMemo(() => {
    const gradePart =
      filterGrade !== '' ? filterGrade : String(t('operationalPlanning.filterGradeAll'));
    const groupePart =
      filterGroupe !== ''
        ? String(t(`operationalPlanning.filterGroupeGr${filterGroupe}` as const))
        : String(t('operationalPlanning.filterGroupeAll'));
    return String(t('operationalPlanning.filterActivePrint'))
      .replace(/\{\{grade\}\}/g, gradePart)
      .replace(/\{\{groupe\}\}/g, groupePart);
  }, [filterGrade, filterGroupe, t]);

  const planningServiceKey = useMemo(
    () => (buildingType ? getPlanningServiceKeyFromBuildingType(buildingType) : null),
    [buildingType]
  );

  const legend = useMemo((): StatutPlanningRowBase[] => {
    if (planningServiceKey) {
      const rows = statuts.statuts_planning_by_service[planningServiceKey];
      if (Array.isArray(rows) && rows.length > 0) return sortPlanningLegendRows(rows);
    }
    return dedupePlanningLegendAllServices();
  }, [planningServiceKey]);

  useEffect(() => {
    if (!isOpen || legend.length === 0) return;
    const ok = legend.some((s) => s.nom.toUpperCase() === selectedStatut.toUpperCase());
    if (ok) return;
    const def =
      legend.find((s) => s.nom.toUpperCase() === DEFAULT_PLANNING_NOM.toUpperCase()) ?? legend[0];
    setSelectedStatut(def.nom);
  }, [isOpen, legend, selectedStatut]);

  const load = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    setError(null);
    setPersistError(null);
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/buildings/personnel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success || !Array.isArray(data.data)) {
        setError(data.error || t('operationalPlanning.loadError'));
        setRows([]);
        return;
      }
      setRows(data.data as PlanningPersonnelRow[]);
    } catch {
      setError(t('operationalPlanning.networkError'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [isOpen, onClose]);

  const persistWeekDay = useCallback(
    async (person: PlanningPersonnelRow, dayIndex: number, statut: string) => {
      const token = localStorage.getItem('token');
      if (!token) return;
      const days = [...effectiveWeekDays(person, weekKey, prevWeekKey)];
      days[dayIndex] = statut.trim() || DEFAULT_PLANNING_NOM;
      setSavingId(person.id);
      setPersistError(null);
      try {
        const res = await fetch(`${getApiBaseUrl(true)}/buildings/personnel/${person.id}/planning-week`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ weekStart: weekKey, days }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success || !data.data) {
          setPersistError(data.error || t('operationalPlanning.saveError'));
          return;
        }
        const upd = data.data as PlanningPersonnelRow;
        setRows((prev) =>
          prev.map((r) =>
            Number(r.id) === Number(upd.id)
              ? {
                  ...r,
                  planning_statut_nom: upd.planning_statut_nom ?? r.planning_statut_nom,
                  planning_weeks_json: upd.planning_weeks_json ?? r.planning_weeks_json,
                }
              : r
          )
        );
      } catch {
        setPersistError(t('operationalPlanning.networkError'));
      } finally {
        setSavingId(null);
      }
    },
    [weekKey, prevWeekKey, t]
  );

  const gridCols = `200px repeat(${WEEK_DAY_COUNT}, minmax(56px, 1fr))`;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-gray-900/40 p-3 backdrop-blur-md sm:p-4 print:fixed print:inset-0 print:z-[9999] print:bg-white print:p-3 print:backdrop-blur-none"
      role="presentation"
      onClick={onClose}
    >
      <div
        id="operational-planning-root"
        role="dialog"
        aria-modal="true"
        aria-labelledby="operational-planning-title"
        className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-gray-700 bg-gray-900 text-gray-100 shadow-2xl print:max-h-none print:max-w-none print:w-full print:overflow-visible print:border print:border-gray-300 print:bg-white print:text-gray-900 print:shadow-none"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex-shrink-0 border-b border-gray-700 bg-gray-800/50 px-3 py-3 sm:px-4 print:hidden">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h1 id="operational-planning-title" className="text-base font-bold tracking-tight text-white md:text-lg">
                {t('operationalPlanning.title')}
              </h1>
              {buildingName ? (
                <p className="mt-0.5 text-sm text-gray-400">
                  {String(t('operationalPlanning.buildingScope')).replace(/\{\{name\}\}/g, buildingName)}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="whitespace-nowrap text-sm text-gray-400">{t('operationalPlanning.period')}</label>
              <select
                className="rounded border border-gray-600 bg-gray-800 px-2 py-1.5 text-sm text-gray-200"
                disabled
                value="weekly"
                aria-label={t('operationalPlanning.period')}
              >
                <option value="weekly">{t('operationalPlanning.weekly')}</option>
              </select>
              <div className="flex items-center rounded border border-gray-600 bg-gray-800">
                <button
                  type="button"
                  className="px-2 py-1.5 text-gray-300 hover:bg-gray-700"
                  onClick={() => setDay((d) => addLocalDays(startOfWeekMonday(d), -7))}
                  aria-label={t('operationalPlanning.prevWeek')}
                >
                  ‹
                </button>
                <input
                  type="date"
                  className="border-0 bg-transparent px-1 py-1 text-sm text-gray-200 outline-none [color-scheme:dark]"
                  value={toLocalDateInputValue(day)}
                  onChange={(e) => {
                    if (e.target.value) setDay(parseLocalDateInput(e.target.value));
                  }}
                />
                <button
                  type="button"
                  className="px-2 py-1.5 text-gray-300 hover:bg-gray-700"
                  onClick={() => setDay((d) => addLocalDays(startOfWeekMonday(d), 7))}
                  aria-label={t('operationalPlanning.nextWeek')}
                >
                  ›
                </button>
              </div>
              <button
                type="button"
                className="rounded border border-gray-600 bg-gray-700 px-3 py-1.5 text-sm text-white hover:bg-gray-600"
                onClick={() => window.print()}
              >
                {t('operationalPlanning.print')}
              </button>
              <button
                type="button"
                className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
                onClick={onClose}
              >
                {t('operationalPlanning.close')}
              </button>
            </div>
          </div>

          <p className="mt-2 text-xs text-gray-500">{t('operationalPlanning.paintHint')}</p>
          <p className="mt-1 text-xs text-amber-200/90">{t('operationalPlanning.inheritHint')}</p>

          <div className="mt-3 flex flex-wrap justify-end gap-2">
            {legend.map((s) => {
              const code = s.code || String(s.id);
              const active = s.nom.toUpperCase() === selectedStatut.toUpperCase();
              return (
                <button
                  key={`legend-${s.nom}`}
                  type="button"
                  onClick={() => setSelectedStatut(s.nom)}
                  className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold shadow-sm transition outline-none ${
                    active ? 'ring-2 ring-blue-400 ring-offset-2 ring-offset-gray-900' : 'hover:opacity-90'
                  }`}
                  style={{
                    backgroundColor: s.couleurs.backgroundColor,
                    color: s.couleurs.textColor,
                  }}
                  aria-pressed={active}
                >
                  [{code}] {s.nom}
                </button>
              );
            })}
          </div>
          {persistError ? <p className="mt-2 text-center text-sm text-red-400">{persistError}</p> : null}
        </header>

        <div className="min-h-0 flex-1 overflow-auto bg-gray-950/40 p-3 md:p-4 print:overflow-visible print:bg-white print:p-0">
          {loading && (
            <p className="py-10 text-center text-gray-500 print:hidden">{t('operationalPlanning.loading')}</p>
          )}
          {error && !loading && (
            <p className="py-6 text-center text-red-400 print:hidden">{error}</p>
          )}
          {!loading && !error && sorted.length === 0 && (
            <p className="py-10 text-center text-gray-500 print:hidden">{t('operationalPlanning.empty')}</p>
          )}
          {!loading && !error && sorted.length > 0 && (
            <div
              id="operational-planning-print"
              className="planning-print-root mx-auto max-w-[100%] rounded-lg border border-gray-600 bg-gray-800/30 print:border-gray-400 print:bg-white"
            >
              <div className="mb-0 hidden border-b border-gray-300 pb-3 text-gray-900 print:block">
                <h2 className="text-lg font-bold">{t('operationalPlanning.title')}</h2>
                {buildingName ? (
                  <p className="mt-1 text-sm text-gray-700">
                    {String(t('operationalPlanning.buildingScope')).replace(/\{\{name\}\}/g, buildingName)}
                  </p>
                ) : null}
              </div>

              <div className="border-b border-amber-500/30 bg-amber-500/15 px-3 py-2 text-center text-sm font-medium text-amber-100 print:border-amber-400 print:bg-amber-50 print:text-gray-900">
                {formatWeekBannerFr(weekStart)}
              </div>
              <p className="hidden px-3 py-1.5 text-center text-[11px] text-gray-700 print:block">{filterSummaryPrint}</p>

              <div
                className="grid border-b border-gray-600 bg-gray-800 text-[11px] font-medium text-gray-200 md:text-xs print:border-gray-400 print:bg-gray-200 print:text-gray-900"
                style={{ gridTemplateColumns: gridCols }}
              >
                <div className="max-w-[220px] border-r border-gray-600 px-2 py-2 text-left print:border-gray-400">
                  <div className="font-medium text-gray-200 print:text-gray-900">
                    {t('operationalPlanning.personnelColumn')}
                  </div>
                  <div className="mt-2 flex flex-col gap-2 print:hidden">
                    <label className="block text-[10px] font-normal uppercase tracking-wide text-gray-500">
                      {t('operationalPlanning.filterGrade')}
                      <select
                        className="mt-0.5 w-full rounded border border-gray-600 bg-gray-900 py-1 pl-1 pr-0 text-[11px] text-gray-200"
                        value={filterGrade}
                        onChange={(e) => setFilterGrade(e.target.value)}
                        aria-label={t('operationalPlanning.filterGrade')}
                      >
                        <option value="">{t('operationalPlanning.filterGradeAll')}</option>
                        {gradeOptions.map((g) => (
                          <option key={g} value={g}>
                            {g}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block text-[10px] font-normal uppercase tracking-wide text-gray-500">
                      {t('operationalPlanning.filterGroupe')}
                      <select
                        className="mt-0.5 w-full rounded border border-gray-600 bg-gray-900 py-1 pl-1 pr-0 text-[11px] text-gray-200"
                        value={filterGroupe}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFilterGroupe(v === '' ? '' : (v as PlanningGroupeKey));
                        }}
                        aria-label={t('operationalPlanning.filterGroupe')}
                      >
                        <option value="">{t('operationalPlanning.filterGroupeAll')}</option>
                        {PLANNING_GROUPE_KEYS.map((k) => (
                          <option key={k} value={k}>
                            {t(`operationalPlanning.filterGroupeGr${k}` as const)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
                {dayColumns.map((colDate) => {
                  const { weekday, dayMonth } = formatDayHeaderFr(colDate);
                  const isTodayCol = colDate.getTime() === todayStart;
                  return (
                    <div
                      key={colDate.getTime()}
                      className={`flex flex-col items-center justify-center border-r border-gray-600 px-1 py-2 text-center last:border-r-0 print:border-gray-400 ${
                        isTodayCol
                          ? 'relative z-[1] bg-amber-500/80 shadow-[inset_0_0_0_3px_#fbbf24] print:bg-amber-400 print:shadow-[inset_0_0_0_4px_#78350f]'
                          : ''
                      }`}
                    >
                      <span
                        className={`font-semibold capitalize ${
                          isTodayCol ? 'text-amber-950 print:text-amber-950' : ''
                        }`}
                      >
                        {weekday}
                      </span>
                      <span
                        className={`capitalize ${
                          isTodayCol
                            ? 'font-bold text-amber-950 print:text-amber-950'
                            : 'text-gray-400 print:text-gray-700'
                        }`}
                      >
                        {dayMonth}
                      </span>
                      {isTodayCol ? (
                        <span
                          className="mt-1 max-w-[100%] rounded-sm border border-amber-900/50 bg-amber-700 px-1 py-0.5 text-[8px] font-extrabold uppercase leading-tight tracking-wide text-white print:border-amber-950 print:bg-amber-800 print:text-amber-50 md:text-[9px]"
                          style={printColorAdjustStyle}
                        >
                          {t('operationalPlanning.todayBadge')}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {displayRows.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500 print:hidden">
                  {t('operationalPlanning.noFilterMatch')}
                </p>
              ) : null}
              {displayRows.map((p) => {
                const daysForRow = effectiveWeekDays(p, weekKey, prevWeekKey);
                const grp = normalizePlanningGroupeValue(p.groupe);
                return (
                  <div
                    key={p.id}
                    className="grid border-b border-gray-700 bg-gray-900/60 last:border-b-0 print:break-inside-avoid print:border-gray-300 print:bg-white"
                    style={{ gridTemplateColumns: gridCols }}
                  >
                    <div className="flex flex-col justify-center border-r border-gray-600 px-2 py-2 text-xs leading-tight print:border-gray-300">
                      <span className="font-semibold text-white print:text-gray-900">
                        {p.prenom} {p.nom}
                      </span>
                      <span className="truncate text-gray-400 print:text-gray-600">{p.grade}</span>
                      {grp ? (
                        <span className="mt-0.5 truncate text-[10px] font-semibold text-amber-200/90 print:text-amber-900">
                          {t(`operationalPlanning.filterGroupeGr${grp}` as const)}
                        </span>
                      ) : null}
                    </div>
                    {dayColumns.map((colDate, dayIndex) => {
                      const isTodayCol = colDate.getTime() === todayStart;
                      const dayNom = daysForRow[dayIndex] || DEFAULT_PLANNING_NOM;
                      const colors = resolvePlanningColors(dayNom, legend);
                      const cellCode = planningCodeLabel(dayNom, legend);
                      const busy = savingId === p.id;
                      return (
                        <button
                          key={`${p.id}-${colDate.getTime()}`}
                          type="button"
                          disabled={busy}
                          className="relative z-[1] flex min-h-[44px] items-center justify-center border-r border-gray-700 p-0.5 text-left last:border-r-0 print:pointer-events-none print:min-h-[36px] print:border-gray-300 print:opacity-100 cursor-pointer hover:brightness-110 disabled:cursor-wait disabled:opacity-70"
                          style={{
                            backgroundColor: colors.backgroundColor,
                            color: colors.textColor,
                            opacity: 0.95,
                            ...(isTodayCol ? { boxShadow: todayColumnCellBoxShadow } : {}),
                            ...printColorAdjustStyle,
                          }}
                          title={`${p.prenom} ${p.nom} — ${colDate.toLocaleDateString('fr-FR')} — ${dayNom}`}
                          onClick={() => persistWeekDay(p, dayIndex, selectedStatut)}
                        >
                          <span className="hidden max-w-full text-center text-[9px] font-bold leading-tight print:inline">
                            {cellCode}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OperationalPlanningModal;
